import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Folder, Lock, Maximize2, Minimize2, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { WarningAlert } from 'components/blocks/warning-alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { cn } from 'lib/utils';
import { useCreatePrompt, usePromptLibrary, useUpdatePrompt } from '../hooks/use-prompts';
import type { PromptFolder, PromptItem } from '../types';
import { extractVariables, hasInvalidPromptVariables, isPlainTextWithoutHtml } from '../utils';
const createPromptSchema = (t: (key: string) => string) => {
  const noHtmlMessage = t('HTML_TAGS_NOT_ALLOWED');
  const invalidVariableMessage = t('PROMPT_INVALID_VARIABLE_FORMAT');

  return z.object({
    title: z
      .string()
      .trim()
      .min(1, t('PROMPT_TITLE_REQUIRED'))
      .refine(isPlainTextWithoutHtml, { message: noHtmlMessage }),
    body: z
      .string()
      .trim()
      .min(1, t('PROMPT_CONTENT_REQUIRED'))
      .refine(isPlainTextWithoutHtml, { message: noHtmlMessage })
      .refine((value) => !hasInvalidPromptVariables(value), {
        message: invalidVariableMessage,
      }),
  });
};

type PromptForm = z.infer<ReturnType<typeof createPromptSchema>>;

function RequiredMark() {
  return (
    <span className="text-destructive" aria-hidden="true">
      {' '}
      *
    </span>
  );
}

type AddPromptModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: PromptFolder | null;
  prompt?: PromptItem | null;
};

function insertTextAtCursor(
  textarea: HTMLTextAreaElement,
  currentBody: string,
  insertText: string,
  cursorOffset: number
) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const nextBody = `${currentBody.slice(0, start)}${insertText}${currentBody.slice(end)}`;
  const cursor = start + cursorOffset;

  return { nextBody, cursor };
}

export function AddPromptModal({ open, onOpenChange, folder, prompt }: Readonly<AddPromptModalProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: library } = usePromptLibrary();
  const { mutateAsync: createPrompt, isPending: isCreating } = useCreatePrompt();
  const { mutateAsync: updatePrompt, isPending: isUpdating } = useUpdatePrompt();
  const isEditing = !!prompt;
  const isOwner = useIsOwner(prompt);
  const canEdit = prompt ? isOwner : true;
  const promptSchema = useMemo(() => createPromptSchema(t), [t]);

  const form = useForm<PromptForm>({
    resolver: zodResolver(promptSchema),
    defaultValues: {
      title: '',
      body: '',
    },
  });

  const bodyValue = form.watch('body');
  const variables = useMemo(() => extractVariables(bodyValue || ''), [bodyValue]);

  useEffect(() => {
    if (!open) return;
    form.reset({
      title: prompt?.title || '',
      body: prompt?.body || '',
    });
    setIsExpanded(false);
  }, [form, open, prompt]);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      form.reset({ title: '', body: '' });
      setIsExpanded(false);
    }
  };

  const focusBodyAt = (cursor: number) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const insertEmptyVariable = () => {
    const token = '{{}}';
    const textarea = bodyRef.current;
    const currentBody = form.getValues('body') || '';

    if (textarea) {
      const { nextBody, cursor } = insertTextAtCursor(textarea, currentBody, token, 2);
      form.setValue('body', nextBody, { shouldValidate: true, shouldDirty: true });
      focusBodyAt(cursor);
      return;
    }

    form.setValue('body', currentBody ? `${currentBody}${token}` : token, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const focusVariableInBody = (variable: string) => {
    const body = form.getValues('body') || '';
    const patterns = [`{{${variable}}}`, `{{ ${variable} }}`, `{{${variable} }}`, `{{ ${variable}}}`];

    for (const pattern of patterns) {
      const index = body.indexOf(pattern);
      if (index === -1) continue;

      const textarea = bodyRef.current;
      if (!textarea) return;

      textarea.focus();
      textarea.setSelectionRange(index, index + pattern.length);
      return;
    }
  };

  const onSubmit = async (values: PromptForm) => {
    if (!folder || !canEdit) return;

    try {
      if (isEditing && prompt) {
        await updatePrompt({
          id: prompt.id,
          folderId: folder.id,
          title: values.title,
          body: values.body,
        });
        toast({
          variant: 'success',
          title: t('PROMPT_UPDATED'),
          description: t('PROMPT_UPDATED_DESCRIPTION'),
        });
      } else {
        await createPrompt({
          folderId: folder.id,
          title: values.title,
          body: values.body,
        });
        toast({
          variant: 'success',
          title: t('PROMPT_SAVED'),
          description: t('PROMPT_SAVED_DESCRIPTION'),
        });
      }
      handleOpenChange(false);
    } catch (error) {
      toast({
        title: isEditing ? t('FAILED_TO_UPDATE_PROMPT') : t('FAILED_TO_SAVE_PROMPT'),
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN'),
        variant: 'destructive',
      });
    }
  };

  const displayFolder = folder || library?.folders.find((item) => item.isDefault) || null;
  const folderLabel = displayFolder?.isDefault
    ? t('PRIVATE') || displayFolder.name
    : displayFolder?.name || t('PRIVATE');
  const isPending = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className={cn(
          'left-[50%] top-[4vh] flex w-[calc(100vw-1.5rem)] -translate-x-1/2 translate-y-0 flex-col gap-0 overflow-hidden p-0',
          'max-h-[min(92dvh,100svh)]',
          isExpanded
            ? 'h-[min(92dvh,100svh)] max-w-[min(96vw,900px)]'
            : 'max-w-2xl'
        )}
      >
        <DialogHeader className="shrink-0 space-y-4 border-b border-border px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <DialogTitle>
                {!isEditing
                  ? t('ADD_PROMPT') || 'Add prompt'
                  : canEdit
                    ? t('EDIT_PROMPT') || 'Edit prompt'
                    : t('VIEW_PROMPT') || 'View prompt'}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {isEditing ? t('EDIT_PROMPT_DESCRIPTION') : t('ADD_PROMPT_DESCRIPTION')}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => handleOpenChange(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t('CLOSE')}</span>
            </Button>
          </div>

          {/* <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm">
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <Folder className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {folderLabel}
            </span>
            <span className="hidden h-4 w-px bg-border sm:block" aria-hidden="true" />
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              {t('ONLY_YOU')}
            </span>
          </div> */}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
            <div
              className={cn(
                'flex min-h-0 flex-1 flex-col gap-5 px-6 py-5',
                isExpanded
                  ? 'overflow-hidden'
                  : 'overflow-y-auto overscroll-contain'
              )}
            >
              {!canEdit ? (
                <WarningAlert>
                  {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_FOLDER') ||
                    'You are not the owner of this folder.'}
                </WarningAlert>
              ) : null}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="shrink-0">
                    <FormLabel>
                      {t('PROMPT_TITLE')}
                      <RequiredMark />
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('PROMPT_TITLE_PLACEHOLDER')}
                        disabled={!canEdit || isPending}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem
                    className={cn('flex min-h-0 flex-col', isExpanded && 'min-h-0 flex-1')}
                  >
                    <FormLabel>
                      {t('PROMPT_CONTENT')}
                      <RequiredMark />
                    </FormLabel>
                    <FormDescription>{t('PROMPT_VARIABLE_PLACEHOLDER_HINT')}</FormDescription>
                    <FormControl>
                      <Textarea
                        {...field}
                        ref={(element) => {
                          field.ref(element);
                          bodyRef.current = element;
                        }}
                        placeholder={t('PROMPT_CONTENT_PLACEHOLDER')}
                        rows={8}
                        height={isExpanded ? undefined : '220px'}
                        disabled={!canEdit || isPending}
                        className={cn(
                          'resize-none overflow-y-auto px-3 py-2.5',
                          isExpanded
                            ? 'min-h-0 flex-1'
                            : 'h-[220px] min-h-[220px] max-h-[220px]'
                        )}
                      />
                    </FormControl>

                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 shrink-0"
                          onClick={insertEmptyVariable}
                          disabled={isPending}
                        >
                          <Plus className="mr-1.5 h-4 w-4" />
                          {t('ADD_VARIABLE')}
                        </Button>
                      ) : null}

                      {variables.length > 0 ? (
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                          {variables.map((variable) => (
                            <button
                              key={variable}
                              type="button"
                              onClick={() => focusVariableInBody(variable)}
                              disabled={!canEdit}
                              className="rounded-md border border-input bg-muted/50 px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                            >
                              {variable}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {t('NO_VARIABLES_YET')}
                        </span>
                      )}

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="ml-auto h-9 w-9 shrink-0"
                        onClick={() => setIsExpanded((value) => !value)}
                        aria-label={isExpanded ? t('COLLAPSE_EDITOR') : t('EXPAND_EDITOR')}
                      >
                        {isExpanded ? (
                          <Minimize2 className="h-4 w-4" />
                        ) : (
                          <Maximize2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="shrink-0 gap-2 border-t border-border px-6 py-4 sm:gap-3">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t('CANCEL')}
              </Button>
              {canEdit ? (
                <Button type="submit" loading={isPending} disabled={isPending || !folder}>
                  {isEditing ? t('SAVE_CHANGES') : t('SAVE_PROMPT')}
                </Button>
              ) : null}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
